import { type UnifiedJobSearchRequest, type UnifiedJobSearchResult } from '@kalos/kalos-rpc';
import { useFormContext } from 'react-hook-form';
import { z } from 'zod';

import {
  useGetDefaultSearchFormValues,
  useSearchParamsForm,
} from '../../../../hooks/useFormWithSearchParams';
import { type ZodObjectWithModelKeys } from '../../../../tools/typeguargs';

type UnifiedJobSearchFields = Pick<
  UnifiedJobSearchRequest,
  | 'jobNumber'
  | 'departmentId'
  | 'customerName'
  | 'businessName'
  | 'propertyAddress'
  | 'propertyId'
  | 'city'
  | 'state'
  | 'zip'
  | 'jobStatus'
  | 'paymentStatus'
  | 'dateStartedFrom'
  | 'dateStartedTo'
  | 'dateEndedFrom'
  | 'dateEndedTo'
  | 'technicianAssigned'
  | 'poNumber'
  | 'isActive'
  | 'projectsOnly'
  | 'serviceCallsOnly'
  | 'name'
  | 'logNotes'
  | 'isResidential'
  | 'jobTypeId'
  | 'jobSubtypeId'
  | 'servicesNeeded'
  | 'firstName'
  | 'lastName'
  | 'globalSearch'
>;

const datePreprocessor = (val: unknown): Date | undefined => {
  if (val instanceof Date) return val;
  if (typeof val === 'string') {
    // Remove quotes if present and decode URI components
    const cleanVal = val.replace(/"/g, '').replace(/%3A/g, ':');
    const date = new Date(cleanVal);
    return isNaN(date.getTime()) ? undefined : date;
  }
  return undefined;
};

export const unifiedJobSearchScheme = z.object({
  // Primary search
  jobNumber: z.string().optional(),
  departmentId: z.number().optional(),

  // Customer filters
  customerName: z.string().optional(),
  businessName: z.string().optional(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),

  // Property filters
  propertyAddress: z.string().optional(),
  propertyId: z.number().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  zip: z.string().optional(),

  // Job status filters
  jobStatus: z.string().optional(),
  paymentStatus: z.string().optional(),

  // Date filters
  dateStartedFrom: z.preprocess(datePreprocessor, z.date().optional()),
  dateStartedTo: z.preprocess(datePreprocessor, z.date().optional()),
  dateEndedFrom: z.preprocess(datePreprocessor, z.date().optional()),
  dateEndedTo: z.preprocess(datePreprocessor, z.date().optional()),

  // Additional filters
  technicianAssigned: z.string().optional(),
  poNumber: z.string().optional(),
  isActive: z.boolean().optional(),

  // Type filters
  projectsOnly: z.boolean().optional(),
  serviceCallsOnly: z.boolean().optional(),

  // New filters
  name: z.string().optional(), // Brief description
  logNotes: z.string().optional(), // Job notes
  isResidential: z.number().optional(), // Sector: 0=all, 1=residential, 2=commercial
  jobTypeId: z.number().optional(),
  jobSubtypeId: z.number().optional(),
  servicesNeeded: z.string().optional(), // Description search
  globalSearch: z.string().optional(), // Global search across multiple fields
}) satisfies ZodObjectWithModelKeys<UnifiedJobSearchFields>;

export type UnifiedJobSearchScheme = z.infer<typeof unifiedJobSearchScheme>;

/**
 * Route for a unified search result: parentId > 0 → project, else → service call (job).
 * Project route expects primaryEventId as the param value (see route comment / ProjectsSearch).
 */
export const getResultRoute = (result: UnifiedJobSearchResult) => {
  const parentId = result.parentId ?? 0;
  if (parentId > 0) {
    const primaryEventId = result.primaryEventId ?? parentId;
    return { to: '/project/$projectId' as const, params: { projectId: primaryEventId } };
  }
  return { to: '/jobs/$jobId' as const, params: { jobId: result.eventId } };
};

/** True if result is treated as project (parentId > 0). */
export const isResultProject = (result: UnifiedJobSearchResult) => (result.parentId ?? 0) > 0;

export const defaultUnifiedJobSearchValues: UnifiedJobSearchScheme = {
  jobNumber: '',
  departmentId: undefined,
  customerName: '',
  businessName: '',
  firstName: '',
  lastName: '',
  propertyAddress: '',
  propertyId: undefined,
  city: '',
  state: '',
  zip: '',
  jobStatus: '',
  paymentStatus: '',
  dateStartedFrom: undefined,
  dateStartedTo: undefined,
  dateEndedFrom: undefined,
  dateEndedTo: undefined,
  technicianAssigned: '',
  poNumber: '',
  isActive: undefined,
  projectsOnly: false,
  serviceCallsOnly: false,
  name: '',
  logNotes: '',
  isResidential: undefined,
  jobTypeId: undefined,
  jobSubtypeId: undefined,
  servicesNeeded: '',
  globalSearch: '',
};

export const useUnifiedJobSearchForm = ({
  isSelector,
  disableParamsSync = false,
}: {
  isSelector: boolean;
  disableParamsSync?: boolean;
}) => {
  return useSearchParamsForm({
    schema: unifiedJobSearchScheme,
    defaultValues: useGetDefaultSearchFormValues(
      unifiedJobSearchScheme,
      defaultUnifiedJobSearchValues,
    ),
    disableParamsSync: isSelector || disableParamsSync,
    defaultResetValues: defaultUnifiedJobSearchValues,
  });
};

export const useUnifiedJobSearchFormContext = () => {
  return useFormContext<UnifiedJobSearchScheme>();
};
